import { Component, OnInit, OnDestroy, HostBinding, ChangeDetectionStrategy } from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { BreakpointObserver, Breakpoints } from "@angular/cdk/layout";

import { Subscription, Observable } from "rxjs";
import { take, map } from "rxjs/operators";

import { LogoUploadModalComponent } from "@onsip/web/features/shared/components/imageUploader/uploadModal/logo-upload-modal.component";
import { TranslateService } from "@ngx-translate/core";
import { TeamPageSettingsService } from "./team-page-settings.service";
import { NoAuthOrganizationSummaryService } from "@onsip/common/services/api/resources/noAuthOrganizationSummary/no-auth-organization-summary.service";
import { OrganizationCustomizationService } from "@onsip/common/services/api/resources/organizationCustomization/organization-customization.service";

@Component({
  selector: "onsip-organization",
  templateUrl: "organization.component.html",
  styleUrls: ["./organization.component.scss"], // share component-level css with profile
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class OrganizationComponent implements OnInit, OnDestroy {
  @HostBinding("class.onsip-grid-content")
  _dontUse = true;

  blurb = "";
  maxBlurbLength = 500;
  setBioTranslation = "";

  greeting = "";
  maxGreetingLength = 75;
  useCustomGreeting = false;
  setGreetingTranslation = "";

  organizationName = "";
  organizationId = "";
  logo = "";
  _color = "";
  color!: Observable<string>;

  apiUser: any;
  teamPageDomain = "";

  isSmall!: Observable<boolean>;

  private unsubscriber = new Subscription();

  constructor(
    private settings: TeamPageSettingsService,
    private translate: TranslateService,
    private dialog: MatDialog,
    private breakpoint: BreakpointObserver,
    private noAuthOrgSummary: NoAuthOrganizationSummaryService,
    private organizationCustomization: OrganizationCustomizationService
  ) {}

  ngOnInit(): void {
    this.setBioTranslation = this.translate.instant("ONSIP_I18N.NO_BIO_HAS_BEEN_SET");
    this.setGreetingTranslation = this.translate.instant("ONSIP_I18N.NO_GREETING_HAS_BEEN_SET");

    this.unsubscriber.add(
      this.settings.teamInfoSubject.subscribe(val => {
        this.blurb = val.blurb;
        this.greeting = val.greeting;
        this.useCustomGreeting = val.useCustomGreeting;
        this.organizationName = val.organizationName;
        this.organizationId = val.organizationId;
        this.logo = val.logo;
        this._color = val.color;
      })
    );

    this.color = this.organizationCustomization.organization.pipe(
      map(organizationCustomization => {
        const accentColor = organizationCustomization.organizationInfo?.teamPageInfo?.accentColor;
        return typeof accentColor === "string" ? accentColor : "#6b80ff"; // lazy just hardcode from teampage color picker
      })
    );

    this.unsubscriber.add(
      this.noAuthOrgSummary.orgState
        .pipe(map(state => state[0]?.teamPageDomain))
        .subscribe(teamPageDomain => (this.teamPageDomain = teamPageDomain))
    );

    this.isSmall = this.breakpoint
      .observe([Breakpoints.XSmall, Breakpoints.Small])
      .pipe(map(bpState => bpState.matches));
  }

  ngOnDestroy(): void {
    this.unsubscriber.unsubscribe();
  }

  submit(): void {
    this.settings.setTeamInfoProperty([
      { property: "blurb", value: this.blurb },
      { property: "greeting", value: this.greeting },
      { property: "color", value: this._color }
    ]);
    this.settings.submit();
  }

  onColorChange(colorChangeEvent: string): void {
    this._color = colorChangeEvent;
    this.submit();
  }

  openUploadLogoModal(): void {
    const dialogRef = this.dialog.open(LogoUploadModalComponent, {
      panelClass: ["mat-typography", "onsip-dialog-universal-style"]
    });
    this.unsubscriber.add(
      dialogRef
        .afterClosed()
        .pipe(take(1))
        .subscribe(response => {
          if (response && response.doSave) {
            this.logo = response.image;
            this.settings.setTeamInfoProperty([{ property: "logo", value: this.logo }]);
          }
        })
    );
  }
}
